/**
 * Module-scoped Component Rules section.
 *
 * Rendered on `ModuleFormPage` below the shared `AdminFormPage` layout
 * when the module is being edited. Exposes a raw-JSON CRUD surface for
 * the module's component rules — the side-quest that unblocks Phase 6
 * testing by sidestepping the stubbed NL translate endpoint.
 *
 * Layout:
 *
 * 1. `Card` titled "Component rules" with an "Add rule" action in the
 *    header.
 * 2. `Table` of every rule for the module (fetched via
 *    `useAllComponentRulesList`). Columns: component, name, description,
 *    status (colour `Badge`), priority, row actions.
 * 3. Row actions dropdown with Edit, Activate (draft-only), Archive
 *    (active-only), Delete (destructive).
 * 4. A confirm `Modal` for destructive actions.
 * 5. A `ModuleRuleFormModal` for create and edit.
 *
 * If `getComponentsByModule(moduleSlug)` returns an empty list, the
 * section short-circuits to an info `Alert` instead of querying the
 * backend — there is nothing to configure.
 */

import { useMemo, useState } from 'react';
import {
  CheckCircle2,
  MoreHorizontal,
  Pencil,
  Trash2,
  Archive,
} from 'lucide-react';

import { Button, DropdownMenu } from '@components/base/actions';
import { Table } from '@components/base/data';
import { Badge, Icon, Text } from '@components/base/display';
import { Alert, Modal, Spinner, useToast } from '@components/base/feedback';
import { Card, HStack, VStack } from '@components/base/layout';
import {
  getComponent,
  getComponentsByModule,
  type RuleStatus,
} from '@components/shared/configurable';

import type { ComponentRuleDTO } from '../../api/componentRulesService';
import {
  useAllComponentRulesList,
  useDeleteComponentRule,
  useUpdateComponentRuleStatus,
} from '../../hooks/componentRules';

import { ModuleRuleFormModal } from './ModuleRuleFormModal';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface ModuleRulesSectionProps {
  /**
   * Slug of the module being edited — matches
   * `ComponentRegistration.module`. Used to filter the registry and
   * passed through to the form modal for future module-aware API
   * clients.
   */
  readonly moduleSlug: string;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Translate a {@link RuleStatus} into the nearest design-system badge
 * variant. Shared with the per-component config page's mapping and
 * kept in sync by convention — not imported to avoid a cross-page
 * dependency.
 */
function statusBadgeVariant(
  status: RuleStatus,
): 'default' | 'success' | 'warning' | 'error' | 'info' | 'primary' {
  switch (status) {
    case 'active':
      return 'success';
    case 'archived':
      return 'default';
    case 'draft':
    default:
      return 'info';
  }
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * Render the module-scoped rules CRUD surface. The prop is stable
 * within a render so the registry lookup inside `useMemo` stays cheap.
 */
export function ModuleRulesSection({
  moduleSlug,
}: Readonly<ModuleRulesSectionProps>) {
  const { toast } = useToast();

  // Registered components for this module. `useMemo` keeps the array
  // reference stable across renders so the modal props do not force
  // a re-render when the parent re-runs.
  const availableComponents = useMemo(
    () => getComponentsByModule(moduleSlug),
    [moduleSlug],
  );

  // ---- State ---------------------------------------------------------

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingRule, setEditingRule] = useState<ComponentRuleDTO | null>(null);
  const [ruleToDelete, setRuleToDelete] = useState<ComponentRuleDTO | null>(
    null,
  );

  // ---- Query ---------------------------------------------------------

  // Fetch unconditionally — the "no components" short-circuit happens
  // in the JSX, so the query always runs when the page is mounted.
  // This keeps the hook call order stable and avoids a refetch storm
  // on the first render that registers a new component.
  const rulesQuery = useAllComponentRulesList();

  // ---- Mutations -----------------------------------------------------

  const statusMutation = useUpdateComponentRuleStatus();
  const deleteMutation = useDeleteComponentRule();

  // ---- Guard: no components registered -------------------------------

  if (availableComponents.length === 0) {
    return (
      <Alert variant="info" title="No configurable components">
        No components registered for this module. Nothing to configure.
      </Alert>
    );
  }

  // ---- Derived data --------------------------------------------------

  const rules = rulesQuery.data ?? [];

  // ---- Handlers ------------------------------------------------------

  const handleAddRule = () => {
    setEditingRule(null);
    setIsFormOpen(true);
  };

  const handleEditRule = (rule: ComponentRuleDTO) => {
    setEditingRule(rule);
    setIsFormOpen(true);
  };

  const handleFormClose = () => {
    setIsFormOpen(false);
    setEditingRule(null);
  };

  const handleFormSaved = () => {
    void rulesQuery.refetch();
    toast({
      variant: 'success',
      title:
        editingRule !== null ? 'Rule updated' : 'Rule created',
    });
  };

  const handleActivate = (rule: ComponentRuleDTO) => {
    statusMutation.mutate(
      { id: rule.id, status: 'active', componentId: rule.component_id },
      {
        onSuccess: () => {
          void rulesQuery.refetch();
          toast({ variant: 'success', title: 'Rule activated' });
        },
        onError: (err) => {
          toast({
            variant: 'error',
            title: 'Failed to activate rule',
            message:
              err instanceof Error ? err.message : 'Unexpected error.',
          });
        },
      },
    );
  };

  const handleArchive = (rule: ComponentRuleDTO) => {
    statusMutation.mutate(
      { id: rule.id, status: 'archived', componentId: rule.component_id },
      {
        onSuccess: () => {
          void rulesQuery.refetch();
          toast({ variant: 'success', title: 'Rule archived' });
        },
        onError: (err) => {
          toast({
            variant: 'error',
            title: 'Failed to archive rule',
            message:
              err instanceof Error ? err.message : 'Unexpected error.',
          });
        },
      },
    );
  };

  const handleConfirmDelete = () => {
    if (ruleToDelete === null) return;
    deleteMutation.mutate(
      { id: ruleToDelete.id, componentId: ruleToDelete.component_id },
      {
        onSuccess: () => {
          void rulesQuery.refetch();
          toast({ variant: 'success', title: 'Rule deleted' });
          setRuleToDelete(null);
        },
        onError: (err) => {
          toast({
            variant: 'error',
            title: 'Failed to delete rule',
            message:
              err instanceof Error ? err.message : 'Unexpected error.',
          });
        },
      },
    );
  };

  // ---- Render --------------------------------------------------------

  return (
    <>
      <Card>
        <Card.Header
          title="Component rules"
          subtitle="Raw-JSON CRUD. For the NL-driven per-component flow, open each component's config page."
          action={
            <Button variant="primary" size="sm" onClick={handleAddRule}>
              Add rule
            </Button>
          }
        />
        <Card.Content>
          <RulesTable
            rulesQuery={rulesQuery}
            rules={rules}
            onEdit={handleEditRule}
            onActivate={handleActivate}
            onArchive={handleArchive}
            onRequestDelete={setRuleToDelete}
          />
        </Card.Content>
      </Card>

      {/* Add / Edit modal */}
      <ModuleRuleFormModal
        open={isFormOpen}
        onClose={handleFormClose}
        moduleSlug={moduleSlug}
        availableComponents={availableComponents}
        editingRule={editingRule ?? undefined}
        onSaved={handleFormSaved}
      />

      {/* Delete confirm modal */}
      <Modal
        open={ruleToDelete !== null}
        onClose={() => {
          setRuleToDelete(null);
        }}
        size="sm"
      >
        <Modal.Header title="Delete rule" />
        <Modal.Body>
          <Text>
            Are you sure you want to delete{' '}
            <Text as="span" weight="bold">
              {ruleToDelete?.name ?? ''}
            </Text>
            ? This cannot be undone.
          </Text>
        </Modal.Body>
        <Modal.Footer>
          <HStack gap={2} justify="end">
            <Button
              variant="secondary"
              onClick={() => {
                setRuleToDelete(null);
              }}
              disabled={deleteMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              loading={deleteMutation.isPending}
              onClick={handleConfirmDelete}
            >
              Delete
            </Button>
          </HStack>
        </Modal.Footer>
      </Modal>
    </>
  );
}

// ---------------------------------------------------------------------------
// Sub-section: Rules table
// ---------------------------------------------------------------------------

interface RulesTableProps {
  readonly rulesQuery: ReturnType<typeof useAllComponentRulesList>;
  readonly rules: ComponentRuleDTO[];
  readonly onEdit: (rule: ComponentRuleDTO) => void;
  readonly onActivate: (rule: ComponentRuleDTO) => void;
  readonly onArchive: (rule: ComponentRuleDTO) => void;
  readonly onRequestDelete: (rule: ComponentRuleDTO) => void;
}

/**
 * Renders the rules table, including loading / error / empty states.
 *
 * Extracted so the main section component stays legible. The spinner
 * is shown inside the table body — the rest of the page remains
 * interactive while rules are loading, per the brief.
 */
function RulesTable({
  rulesQuery,
  rules,
  onEdit,
  onActivate,
  onArchive,
  onRequestDelete,
}: Readonly<RulesTableProps>) {
  if (rulesQuery.isLoading) {
    return (
      <VStack align="center" justify="center" gap={2} className="py-8">
        <Spinner size="md" />
        <Text size="sm" color="muted">
          Loading rules...
        </Text>
      </VStack>
    );
  }

  if (rulesQuery.isError) {
    return (
      <Alert variant="error" title="Failed to load rules">
        {rulesQuery.error instanceof Error
          ? rulesQuery.error.message
          : 'An unexpected error occurred while loading rules.'}
      </Alert>
    );
  }

  if (rules.length === 0) {
    return (
      <Alert variant="info" title="No rules yet">
        Use the &ldquo;Add rule&rdquo; button above to create your first rule.
      </Alert>
    );
  }

  return (
    <Table>
      <Table.Head>
        <Table.Row>
          <Table.HeaderCell>Component</Table.HeaderCell>
          <Table.HeaderCell>Name</Table.HeaderCell>
          <Table.HeaderCell>Description</Table.HeaderCell>
          <Table.HeaderCell>Status</Table.HeaderCell>
          <Table.HeaderCell>Priority</Table.HeaderCell>
          <Table.HeaderCell align="right">Actions</Table.HeaderCell>
        </Table.Row>
      </Table.Head>
      <Table.Body>
        {rules.map((rule) => {
          const registration = getComponent(rule.component_id);
          const componentLabel =
            registration?.displayName ?? rule.component_id;
          return (
            <Table.Row key={rule.id}>
              <Table.Cell>
                <Text size="sm" weight="medium">
                  {componentLabel}
                </Text>
              </Table.Cell>
              <Table.Cell>
                <Text weight="medium">{rule.name}</Text>
              </Table.Cell>
              <Table.Cell>
                <Text size="sm" color="muted">
                  {rule.description ?? '--'}
                </Text>
              </Table.Cell>
              <Table.Cell>
                <HStack gap={1} align="center">
                  <Badge variant={statusBadgeVariant(rule.status)} size="sm">
                    {rule.status}
                  </Badge>
                  <Badge
                    variant={rule.visibility === 'public' ? 'info' : 'default'}
                    size="sm"
                  >
                    {rule.visibility === 'public' ? 'Public' : 'Private'}
                  </Badge>
                </HStack>
              </Table.Cell>
              <Table.Cell>
                <Text size="sm">{rule.priority}</Text>
              </Table.Cell>
              <Table.Cell align="right">
                <DropdownMenu>
                  <DropdownMenu.Trigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      aria-label={`Actions for ${rule.name}`}
                      leftIcon={<Icon icon={MoreHorizontal} size="sm" />}
                    />
                  </DropdownMenu.Trigger>
                  <DropdownMenu.Content align="end">
                    <DropdownMenu.Item
                      icon={<Icon icon={Pencil} size="sm" />}
                      onSelect={() => {
                        onEdit(rule);
                      }}
                    >
                      Edit
                    </DropdownMenu.Item>
                    {rule.status === 'draft' && (
                      <DropdownMenu.Item
                        icon={<Icon icon={CheckCircle2} size="sm" />}
                        onSelect={() => {
                          onActivate(rule);
                        }}
                      >
                        Activate
                      </DropdownMenu.Item>
                    )}
                    {rule.status === 'active' && (
                      <DropdownMenu.Item
                        icon={<Icon icon={Archive} size="sm" />}
                        onSelect={() => {
                          onArchive(rule);
                        }}
                      >
                        Archive
                      </DropdownMenu.Item>
                    )}
                    <DropdownMenu.Item
                      destructive
                      icon={<Icon icon={Trash2} size="sm" />}
                      onSelect={() => {
                        onRequestDelete(rule);
                      }}
                    >
                      Delete
                    </DropdownMenu.Item>
                  </DropdownMenu.Content>
                </DropdownMenu>
              </Table.Cell>
            </Table.Row>
          );
        })}
      </Table.Body>
    </Table>
  );
}
